import { DECK_GROUPINGS, DECK_SORTS, DECK_VIEWS, type DeckGrouping, type DeckSort, type DeckViewStyle } from '@mtg/shared';
import { useState } from 'react';

/** How decks are shown, remembered in this browser only (each device keeps its own). */
export type DeckPrefs = { view: DeckViewStyle; groupBy: DeckGrouping; sort: DeckSort; stats: boolean };

const KEY = 'mtg.deckView';
const DEFAULTS: DeckPrefs = { view: 'list', groupBy: 'type', sort: 'name', stats: true };

const oneOf = <T extends string>(list: readonly T[], value: unknown, fallback: T): T =>
  list.includes(value as T) ? (value as T) : fallback;

function load(): DeckPrefs {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, unknown>;
    return {
      view: oneOf(DECK_VIEWS, saved.view, DEFAULTS.view),
      groupBy: oneOf(DECK_GROUPINGS, saved.groupBy, DEFAULTS.groupBy),
      sort: oneOf(DECK_SORTS, saved.sort, DEFAULTS.sort),
      stats: typeof saved.stats === 'boolean' ? saved.stats : DEFAULTS.stats,
    };
  } catch {
    // Storage can be blocked (private windows); fall back to the defaults.
    return DEFAULTS;
  }
}

export function useDeckPrefs() {
  const [prefs, setPrefs] = useState(load);
  const update = (changes: Partial<DeckPrefs>) => {
    const next = { ...prefs, ...changes };
    setPrefs(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Not remembered this time; the view still changes.
    }
  };
  return [prefs, update] as const;
}
